import React, { useState } from 'react'
import { cn } from '../../../lib/utils'
import Icon, { type iconTypes } from '../Icon'
import Link from 'next/link'

export type ChipProps = {
  color?: string
  label: string
  variant: 'primary' | 'secondary' | 'destructive' | 'verified' | 'complete'
  icon?: iconTypes
  className?: string
  isLoading?: boolean
  href?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  maxWidth?: string | number
  deleteModeEnabled?: boolean
  handleDelete?: () => void
  isDeleteMode?: boolean
  onDeleteModeChange?: (isDeleteMode: boolean) => void
}

export const Chip: React.FC<ChipProps> = (props) => {
  const {
    label,
    variant,
    icon,
    isLoading,
    href,
    color,
    target,
    maxWidth,
    deleteModeEnabled,
    handleDelete,
    isDeleteMode: externalIsDeleteMode,
    onDeleteModeChange,
  } = props

  const getStyle = () => {
    const style: React.CSSProperties = {}
    if (maxWidth) style.maxWidth = maxWidth
    if (color) style.borderColor = color
    return Object.keys(style).length > 0 ? style : undefined
  }

  const [internalIsDeleteMode, setInternalIsDeleteMode] = useState(false)

  // Use external state if provided, otherwise use internal state
  const isDeleteMode =
    externalIsDeleteMode !== undefined
      ? externalIsDeleteMode
      : internalIsDeleteMode
  const setIsDeleteMode = (value: boolean) => {
    if (externalIsDeleteMode !== undefined) {
      onDeleteModeChange?.(value)
    } else {
      setInternalIsDeleteMode(value)
    }
  }

  const Component = href ? 'a' : 'span'

  const handleChipClick = (e: React.MouseEvent) => {
    if (deleteModeEnabled && handleDelete) {
      e.preventDefault()
      e.stopPropagation()
      setIsDeleteMode(true)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleDelete?.()
    setIsDeleteMode(false)
  }

  const showDeleteState = deleteModeEnabled && handleDelete && isDeleteMode

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        onClick={handleChipClick}
        className={cn(
          'border rounded-lg px-2 h-6 text-xs flex items-center text-nowrap gap-1 relative',
          {
            'text-red-500 border-red-500':
              props.variant === 'destructive' && !color,
            'text-green-500 border-green-500':
              props.variant === 'complete' && !color,
            'text-blue-500 border-blue-500':
              props.variant === 'verified' && !color,
            'border-2 border-primary': props.variant === 'primary' && !color,
            'hover:opacity-80 cursor-pointer': href,
            'ring-2 ring-blue-500 ring-opacity-50': showDeleteState,
          },
          props.className,
        )}
        style={getStyle()}
      >
        <>
          {props.icon && (
            <Icon
              name={props.isLoading ? 'LoaderCircle' : props.icon}
              size={12}
              className={cn(
                props.isLoading ? 'animate-spin' : '',
                'flex-shrink-0',
              )}
            />
          )}
          <span
            className={
              maxWidth ? 'overflow-hidden text-ellipsis whitespace-nowrap' : ''
            }
          >
            {props.label}
          </span>

          {/* changes */}
          {showDeleteState && (
            <button
              onClick={handleDeleteClick}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
              aria-label="Delete chip"
            >
              ×
            </button>
          )}
        </>
      </Link>
    )
  }

  return (
    <Component
      href={href}
      target={target}
      onClick={handleChipClick}
      className={cn(
        'border rounded-lg px-2 h-6 text-xs flex items-center text-nowrap gap-1 relative',
        {
          'text-red-500 border-red-500':
            props.variant === 'destructive' && !color,
          'text-green-500 border-green-500':
            props.variant === 'complete' && !color,
          'text-blue-500 border-blue-500':
            props.variant === 'verified' && !color,
          'border-2 border-primary': props.variant === 'primary' && !color,
          'hover:opacity-80 cursor-pointer': href,
          'ring-2 ring-blue-500 ring-opacity-50': showDeleteState,
          'bg-foreground/10': showDeleteState,
        },
        props.className,
      )}
      style={getStyle()}
    >
      {props.icon && (
        <Icon
          name={props.isLoading ? 'LoaderCircle' : props.icon}
          size={12}
          className={cn(props.isLoading ? 'animate-spin' : '', 'flex-shrink-0')}
        />
      )}
      <span
        className={
          maxWidth ? 'overflow-hidden text-ellipsis whitespace-nowrap' : ''
        }
      >
        {props.label}
      </span>
      {showDeleteState && (
        <button
          onClick={handleDeleteClick}
          className="absolute -top-1 -right-1 w-3 h-3 bg-foreground/70 text-white rounded-full flex items-center justify-center text-xs transition-colors"
          aria-label="Delete chip"
        ></button>
      )}
    </Component>
  )
}
